import { useState } from "react";
import CnpjUtils from "../../../shared/utils/CnpjUtils";
import { throwResponseError } from "../../utils/api-error-utils";
import { getAuthParams } from "../../utils/pacto-rebates-utils";

const formatDailyGrowthRange = range => {
	if (!range) {
		return null;
	}

	return {
		discountPercentage: range.percentualDesconto ?? 0,
		minimumDailyValue: range.valorMinimoDiario ?? 0,
		requiredGrowthValue: range.crescimentoNecessarioReais ?? 0,
		requiredGrowthPercentage: range.percentualCrescimentoNecessario ?? null,
		reached: Boolean(range.alcancada),
		raw: range
	};
};

const formatDailyGrowthSimulationResponse = simulationData => {
	const data = simulationData?.resultado ?? simulationData ?? {};

	const ranges = Array.isArray(data.faixas)
		? data.faixas.map(formatDailyGrowthRange).filter(Boolean)
		: [];

	const nextRange = formatDailyGrowthRange(data.proximaFaixa);

	return {
		cycle: data.ciclo || "",
		calculationMode: data.modoCalculo || "",
		currentDailyAverage: data.mediaDiariaAtual ?? null,
		cycleRevenue: data.faturamentoCiclo ?? 0,
		elapsedBusinessDays: data.diasUteisCorridos ?? 0,
		ranges,
		nextRange,
		raw: data
	};
};

export function useDashboardDailyGrowthSimulation() {
	const [dailyGrowthSimulation, setDailyGrowthSimulation] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const fetchDailyGrowthSimulation = async ({
		document,
		contractId,
		cycle,
		calculationMode
	}) => {
		const cleanDocument = CnpjUtils.cleanAlphanumericCnpj(document || "");

		if (!cleanDocument || !contractId || !cycle) {
			const message = "Informe o documento do cliente, o contrato e o ciclo.";

			setDailyGrowthSimulation(null);
			setError(message);

			return {
				ok: false,
				error: message
			};
		}

		try {
			setLoading(true);
			setError(null);

			const { accessToken, clientId } = getAuthParams();

			const params = new URLSearchParams();

			if (calculationMode) {
				params.append("modoCalculo", calculationMode);
			}

			const queryString = params.toString();
			const encodedCycle = encodeURIComponent(cycle);

			const response = await fetch(
				process.env.REACT_APP_ESSILOR_API +
					`pacto-rebates-dashboard/v1/dashboard/clientes/${cleanDocument}/contratos/${contractId}/ciclos/${encodedCycle}/simulacao-crescimento` +
					(queryString ? `?${queryString}` : ""),
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						"access_token": accessToken,
						"client_id": clientId
					}
				}
			);

			if (!response.ok) {
				await throwResponseError(
					response,
					"Não foi possível carregar a simulação de crescimento diário."
				);
			}

			const data = await response.json();
			const formattedSimulation = formatDailyGrowthSimulationResponse(data);

			setDailyGrowthSimulation(formattedSimulation);
			setError(null);

			return {
				ok: true,
				dailyGrowthSimulation: formattedSimulation
			};
		} catch (requestError) {
			const errorMessage =
				requestError.message ||
				"Não foi possível carregar a simulação de crescimento diário.";

			setDailyGrowthSimulation(null);
			setError(errorMessage);

			return {
				ok: false,
				error: errorMessage
			};
		} finally {
			setLoading(false);
		}
	};

	const clearDailyGrowthSimulation = () => {
		setDailyGrowthSimulation(null);
		setError(null);
	};

	return {
		dailyGrowthSimulation,
		dailyGrowthSimulationLoading: loading,
		dailyGrowthSimulationError: error,
		fetchDailyGrowthSimulation,
		clearDailyGrowthSimulation
	};
}